import { LocalizedString } from '../localized-string.js';
import type { Dayjs } from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import { toEnumFromString, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { cloneDataOrNull, generateUniqueDatabaseId, isSameData } from '../../dto/data-model-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { Currency } from '../currency.js';
import { Price } from '../price.js';
import { CustomerProfile } from '../customer-profile.js';
import { IProductWithSerialNumber } from '../../../contracts/product-with-serial-number.js';
import { GiftCardAttachmentType, GiftCardChannel } from './gift-card-variation.js';
import { TimeZone } from '../../../time/timezone.js';
import { Fee } from '../fee.js';
import { isNotNullOrUndefined } from '../../../utility/null-safety.js';
import { IllegalOperationException } from '../../../exceptions/illegal-operation-exception.js';
import { InvoiceVat } from '../invoicing/invoice-vat.js';

export enum GiftCardStatus {
  Live = 'LIVE',
  Used = 'USED',
  Canceled = 'CANCELED',
  Refunded = 'REFUNDED',
  Aborted = 'ABORTED',
}

export class GiftCard extends DataModel implements IProductWithSerialNumber {
  static readonly TYPE_ID: string = '1566295abff84cbba447783a327e9e9c';
  static readonly SCHEMA_VERSION: number = 1;

  private constructor(
    public readonly uniqueId: string,
    public readonly templateId: string,
    public readonly variationId: string,
    public readonly templateName: LocalizedString,
    public readonly variationName: LocalizedString,
    public readonly businessRef: BusinessModuleRef,
    public readonly customer: CustomerProfile | null,
    public readonly channel: GiftCardChannel,
    public readonly price: Price,
    public readonly maxValue: Price,
    public readonly usedValue: Price,
    public readonly creationDate: Dayjs,
    public readonly expirationDate: Dayjs,
    public readonly serialNumber: string | null,
    public readonly code: string,
    public readonly status: GiftCardStatus,
    public readonly attachmentType: GiftCardAttachmentType | null,
    public readonly orderId: string,
    public readonly orderEntryId: string,
    public readonly cancelledDate: Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly overrideVat: InvoiceVat | null, // Override VAT applied to the gift card.
    public readonly applicationFee: Fee | null, // Fee taken by In Time on top of the ticket price.
  ) {
    super();
  }

  get $typeId(): string {
    return GiftCard.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(GiftCard.SCHEMA_VERSION);
  }

  get isGlobal(): boolean {
    return this.businessRef === null;
  }

  get currency(): Currency {
    return this.maxValue.currency;
  }

  get remainingValue(): Price {
    return this.maxValue.difference(this.usedValue);
  }

  static create(args: {
    uniqueId: string,
    templateId: string,
    variationId: string,
    templateName: LocalizedString,
    variationName: LocalizedString,
    businessRef: BusinessModuleRef,
    customer: CustomerProfile | null,
    channel: GiftCardChannel,
    price: Price,
    maxValue: Price,
    creationDate: Dayjs,
    expirationDate: Dayjs,
    serialNumber: string | null,
    code: string,
    orderId: string,
    orderEntryId: string,
    attachmentType: GiftCardAttachmentType | null,
    overrideVat: InvoiceVat | null,
    applicationFee: Fee | null, // Fee taken by In Time on top of the ticket price.
    usedValue?: Price,
    status?: GiftCardStatus,
    cancelledDate?: Dayjs | null,
    cancelledBy?: string | null,
  }): GiftCard {
    return new GiftCard(
        args.uniqueId,
        args.templateId,
        args.variationId,
        args.templateName.clone(),
        args.variationName.clone(),
        args.businessRef.clone(),
        args.customer?.clone() ?? null,
        args.channel,
        args.price.clone(),
        args.maxValue.clone(),
        (args.usedValue ?? Price.zero(args.maxValue.currency)).clone(),
        args.creationDate.clone(),
        args.expirationDate.clone(),
        args.serialNumber,
        args.code,
        args.status ?? GiftCardStatus.Live,
        args.attachmentType,
        args.orderId,
        args.orderEntryId,
        TimeZone.cloneDateOrNull(args.cancelledDate ?? null),
        args.cancelledBy ?? null,
        args.overrideVat,
        cloneDataOrNull(args.applicationFee),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GiftCard {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      templateId: MapUtils.getString(map, 'templateId'),
      variationId: MapUtils.getString(map, 'variationId'),
      templateName: context.fromData<LocalizedString>(MapUtils.getRecord(map, 'templateName')),
      variationName: context.fromData<LocalizedString>(MapUtils.getRecord(map, 'variationName')),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      customer: context.fromDataOrNull<CustomerProfile>(MapUtils.getRecordOrNull(map, 'customer')),
      channel: toEnumFromString(MapUtils.getString(map, 'channel'), GiftCardChannel),
      price: Price.decode(MapUtils.getString(map, 'price')),
      maxValue: Price.decode(MapUtils.getString(map, 'maxValue')),
      usedValue: Price.decode(MapUtils.getString(map, 'usedValue')),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      expirationDate: context.decodeDate(MapUtils.getInteger(map, 'expirationDate')),
      serialNumber: MapUtils.getStringOrNull(map, 'serialNumber'),
      code: MapUtils.getString(map, 'code'),
      status: toEnumFromString(MapUtils.getString(map, '$status'), GiftCardStatus),
      attachmentType: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'attachmentType'), GiftCardAttachmentType),
      overrideVat: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'overrideVat'), InvoiceVat),
      orderId: MapUtils.getString(map, 'orderId'),
      orderEntryId: MapUtils.getString(map, 'orderEntryId'),
      cancelledDate: context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'cancelledDate')),
      cancelledBy: MapUtils.getStringOrNull(map, 'cancelledBy'),
      applicationFee: context.fromDataOrNull<Fee>(MapUtils.getRecordOrNull(map, 'applicationFee')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'templateId': this.templateId,
      'variationId': this.variationId,
      'templateName': context.toData(this.templateName),
      'variationName': context.toData(this.variationName),
      'businessRef': this.businessRef.encode(),
      'customer': context.toDataOrNull(this.customer),
      'channel': this.channel,
      'price': this.price.encode(),
      'maxValue': this.maxValue.encode(),
      'usedValue': this.usedValue.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'expirationDate': context.encodeDate(this.expirationDate),
      'serialNumber': this.serialNumber,
      'code': this.code,
      '$status': this.status,
      'attachmentType': this.attachmentType,
      'overrideVat': this.overrideVat,
      'orderId': this.orderId,
      'orderEntryId': this.orderEntryId,
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
      'applicationFee': context.toDataOrNull(this.applicationFee),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    templateId = this.templateId,
    variationId = this.variationId,
    templateName = this.templateName.clone(),
    variationName = this.variationName.clone(),
    businessRef = this.businessRef.clone(),
    customer = this.customer?.clone() ?? null,
    channel = this.channel,
    price = this.price.clone(),
    maxValue = this.maxValue.clone(),
    creationDate = TimeZone.cloneDate(this.creationDate),
    expirationDate = TimeZone.cloneDate(this.expirationDate),
    serialNumber = this.serialNumber,
    code = this.code,
    orderId = this.orderId,
    orderEntryId = this.orderEntryId,
    usedValue = this.usedValue.clone(),
    status = this.status,
    attachmentType = this.attachmentType,
    overrideVat = this.overrideVat,
    cancelledDate = TimeZone.cloneDateOrNull(this.cancelledDate),
    cancelledBy = this.cancelledBy,
    applicationFee = cloneDataOrNull(this.applicationFee),
  }: {
    uniqueId?: string,
    templateId?: string,
    variationId?: string,
    templateName?: LocalizedString,
    variationName?: LocalizedString,
    businessRef?: BusinessModuleRef,
    customer?: CustomerProfile | null,
    channel?: GiftCardChannel,
    price?: Price,
    maxValue?: Price,
    creationDate?: Dayjs,
    expirationDate?: Dayjs,
    serialNumber?: string | null,
    code?: string,
    orderId?: string,
    orderEntryId?: string,
    usedValue?: Price,
    status?: GiftCardStatus,
    attachmentType?: GiftCardAttachmentType | null,
    overrideVat?: InvoiceVat | null,
    cancelledDate?: Dayjs | null,
    cancelledBy?: string | null,
    applicationFee?: Fee | null,
  } = {}): this {
    return GiftCard.create({
      uniqueId,
      templateId,
      variationId,
      templateName,
      variationName,
      businessRef,
      customer,
      channel,
      price,
      maxValue,
      creationDate,
      expirationDate,
      serialNumber,
      code,
      orderId,
      orderEntryId,
      usedValue,
      status,
      attachmentType,
      overrideVat,
      cancelledDate,
      cancelledBy,
      applicationFee,
    }) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
      this.templateId === other.templateId &&
      this.variationId === other.variationId &&
      isSameData(this.templateName, other.templateName) &&
      isSameData(this.variationName, other.variationName) &&
      isSameData(this.businessRef, other.businessRef) &&
      isSameData(other.customer, this.customer) &&
      this.channel === other.channel &&
      isSameData(this.price, other.price) &&
      isSameData(this.maxValue, other.maxValue) &&
      isSameData(this.usedValue, other.usedValue) &&
      this.creationDate.valueOf() === other.creationDate.valueOf() &&
      this.expirationDate.valueOf() === other.expirationDate.valueOf() &&
      isSameData(other.applicationFee, this.applicationFee) &&
      this.serialNumber === other.serialNumber &&
      this.code === other.code &&
      this.status === other.status &&
      this.attachmentType === other.attachmentType &&
      this.overrideVat === other.overrideVat &&
      TimeZone.isSameInstantOrNull(other.cancelledDate, this.cancelledDate) &&
      other.cancelledBy === this.cancelledBy &&
      this.orderId === other.orderId &&
      this.orderEntryId === other.orderEntryId;
  }

  setSerialNumber(serialNumber: string): GiftCard {
    if(isNotNullOrUndefined(this.serialNumber)) {
      throw new IllegalOperationException(`Cannot set serial number {${serialNumber}} for gift card {${this.uniqueId}} of order {${this.orderId}} because it already has a serial number {${this.serialNumber}}`);
    }

    return this.copyWith({
      serialNumber: serialNumber,
    });
  }

  copyAsAborted(): this {
    return this.copyWith({ status: GiftCardStatus.Aborted });
  }

  copyAsCanceled(cancelledBy: string | null, cancelledDate: Dayjs, isRefund: boolean): this {
    return this.copyWith({
      status: isRefund ? GiftCardStatus.Refunded : GiftCardStatus.Canceled,
      cancelledDate: TimeZone.cloneDateOrNull(this.cancelledDate) ?? cancelledDate,
      cancelledBy: this.cancelledBy ?? cancelledBy,
    });
  }

  static generateVoucherCode(): string {
    const randomStr = generateUniqueDatabaseId({ length: 15, alphabet: 'NONSIMILAR_CHARACTERS' });
    return `GC-${randomStr.substring(0, 5)}-${randomStr.substring(5, 10)}-${randomStr.substring(10, randomStr.length)}`;
  }
}

export class GiftCardBuilder extends DataModelBuilder<GiftCard> {
  build(context: SerializationContext, map: SerializedData): GiftCard {
    return GiftCard.fromMap(context, map);
  }
}
